/**
 * {@link AudioEngine} — the instance-based Web Audio engine.
 *
 * One engine per app (NOT static, unlike the icespace `AudioManager` it is ported
 * from). It owns:
 *  - the {@link AudioContext} lifecycle + first-gesture unlock,
 *  - a master → {music, sfx} bus graph, each independently gated,
 *  - a {@link CueRegistry} of named cues played through the SFX bus: the
 *    platform's vocabulary cues as digest-pinned samples from the shared SFX
 *    bank (`sfxBank.ts`), and a native game's own procedural recipes,
 *  - two playback gates the shell drives — its lifecycle gate (pause,
 *    recovery, demo) and the document-visibility gate — composed so music
 *    resumes only when every gate is down,
 *  - a single looping-music slot routed through the music bus,
 *  - a ducking helper (drop music under a transient), and
 *  - volume/mute/enable prefs persisted through a {@link KvStorePort}.
 *
 * Nothing here leaks Firebase/Colyseus/Phaser types: the only injected port is
 * `KvStorePort` from `@djgg/core`.
 */
import {
  AUDIO_MEDIA_FORMATS,
  inspectAudioMedia,
  type AudioMediaKind,
  type AudioMediaLoop,
  type AudioMediaMetadata,
  type KvStorePort,
  type Logger,
} from '@djgg/core';
import type { AudioEngineOptions, AudioPrefs, CueParams, CueRecipe } from './types.js';
import {
  DEFAULT_PREFS,
  clampVolume,
  loadPrefs,
  masterGainFor,
  musicGainFor,
  savePrefs,
  sfxGainFor,
} from './prefs.js';
import { CueRegistry } from './registry.js';
import { BusSynthContext } from './synth.js';
import { readPcmSfx, MAX_SFX_BYTES } from './pcmSfx.js';

/** Default music-bus resting gain (music sits quietly under SFX). */
const DEFAULT_MUSIC_VOLUME = 0.4;

/**
 * A sample id. Creator-owned ids (`variantId`) are restricted upstream by
 * `@djgg/core` to `[A-Za-z0-9_-]`; the extra `.` and `/` exist for the
 * platform's `bank/<cue>.<family>.<variant>` namespace, which a creator id can
 * therefore never collide with (see `sfxBank.ts` `bankSampleId`).
 */
const SAMPLE_ID = /^[A-Za-z0-9][A-Za-z0-9_./-]{0,127}$/;

/** Loaded-sample ceiling: the bank's largest possible binding (24 cues × 2
 *  variants) plus a creator's curated binding (32 cues) fit with headroom. */
const MAX_LOADED_SAMPLES = 96;

/** Concurrent sample voices. */
const MAX_SAMPLE_VOICES = 8;

/**
 * Tell the browser this page's Web Audio is PLAYBACK. Without it iOS Safari
 * files Web Audio as "ambient": the ring/silent switch silences it and it
 * follows the ringer, so a game can show the tab's speaker icon while the
 * player hears nothing. The Audio Session API (Safari 16.4+) is the only lever;
 * elsewhere it is absent and this is a no-op. Set before the context exists.
 */
function declarePlaybackSession(): void {
  const session = (globalThis.navigator as { audioSession?: { type: string } } | undefined)?.audioSession;
  if (session) session.type = 'playback';
}

export class AudioEngine {
  private readonly contextFactory: () => AudioContext;
  private readonly musicVolume: number;
  private musicLevel = 1;
  private sfxLevel = 1;
  private readonly log?: Logger;

  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private synth: BusSynthContext | null = null;

  private prefs: AudioPrefs;
  private readonly registry: CueRegistry;

  // Music slot state.
  private musicBuffer: AudioBuffer | null = null;
  private musicSource: AudioBufferSourceNode | null = null;
  private musicPlaying = false;
  private musicLoadEpoch = 0;
  private musicLoop: AudioMediaLoop | null = null;
  /** The shell's lifecycle gate (pause, recovery, demo, departed scene). */
  private musicSuspended = false;
  /** The document-visibility gate (`setDocumentHidden`). */
  private hidden = false;
  private resumeMusic = false;
  private sampleEpoch = 0;
  private samples = new Map<string, AudioBuffer>();
  private sampleRequests = new Map<string, symbol>();
  /** Live sample voices → the sample id each plays, so a per-id unload can
   *  stop exactly its own voices. */
  private sampleSources = new Map<AudioBufferSourceNode, string>();
  private samplesSuspended = false;

  // Ducking state — a pending restore timer + the gain we duck back to.
  private duckTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(opts: AudioEngineOptions & { logger?: Logger } = {}) {
    this.contextFactory = opts.contextFactory ?? (() => new AudioContext());
    this.musicVolume = opts.musicVolume ?? DEFAULT_MUSIC_VOLUME;
    this.log = opts.logger;
    this.prefs = { ...DEFAULT_PREFS, ...opts.defaultPrefs };
    this.registry = new CueRegistry();
  }

  // ── Lifecycle ──────────────────────────────────────────────────

  /**
   * Create the {@link AudioContext} + bus graph. Idempotent. Browsers start the
   * context SUSPENDED until a gesture resumes it; {@link unlock} does that.
   */
  init(): void {
    if (this.ctx) return;
    declarePlaybackSession();
    const ctx = this.contextFactory();
    this.ctx = ctx;

    const master = ctx.createGain();
    master.gain.value = masterGainFor(this.prefs);
    master.connect(ctx.destination);
    this.masterGain = master;

    const music = ctx.createGain();
    music.gain.value = this.musicRestingGain();
    music.connect(master);
    this.musicGain = music;

    const sfx = ctx.createGain();
    sfx.gain.value = sfxGainFor(this.prefs) * this.sfxLevel;
    sfx.connect(master);
    this.sfxGain = sfx;

    this.synth = new BusSynthContext(ctx, sfx);
  }

  /**
   * Create (if needed) AND resume the context — call from a real user gesture
   * (first pointer/key, or an in-game "tap to begin"). While the context is
   * suspended every cue is DROPPED (never queued — see the icespace note: queued
   * sounds all fire at once on unlock), so this is what makes audio live. Safe
   * to call often.
   */
  unlock(): void {
    this.init();
    const ctx = this.ctx;
    if (ctx && ctx.state !== 'running') {
      ctx.resume().catch(() => {
        /* still needs a genuine user gesture */
      });
    }
  }

  /** Is the context created and actively running (i.e. cues will sound)? */
  isRunning(): boolean {
    return this.ctx?.state === 'running';
  }

  /**
   * The live {@link AudioContext} IF it exists and is running, else `null`.
   * DROPS (returns null) while suspended rather than queuing — a suspended
   * context has a frozen clock, so anything scheduled at `currentTime` would all
   * fire at once when a later gesture unlocks it. A best-effort resume is kicked
   * off so the next gesture re-arms it.
   */
  private runningCtx(): AudioContext | null {
    const ctx = this.ctx;
    if (!ctx || !this.sfxGain) return null;
    if (ctx.state !== 'running') {
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {
          /* requires user gesture */
        });
      }
      return null;
    }
    return ctx;
  }

  /** Release the context and all graph nodes. The engine can be `init`'d again. */
  async dispose(): Promise<void> {
    this.clearSamples();
    this.musicLoadEpoch++;
    this.stopMusic();
    this.musicSuspended = false;
    if (this.duckTimer) {
      clearTimeout(this.duckTimer);
      this.duckTimer = null;
    }
    const ctx = this.ctx;
    this.ctx = null;
    this.masterGain = null;
    this.musicGain = null;
    this.sfxGain = null;
    this.synth = null;
    this.musicBuffer = null;
    this.musicLoop = null;
    if (ctx) {
      try {
        await ctx.close();
      } catch {
        /* already closed */
      }
    }
  }

  // ── Prefs / volume / mute ──────────────────────────────────────

  /** A copy of the current prefs (mutating it does not affect the engine). */
  getPrefs(): AudioPrefs {
    return { ...this.prefs };
  }

  /**
   * Load persisted prefs from `kv` and apply them to the live buses (if
   * `init`'d). Call once at boot after constructing the engine.
   */
  async loadPrefs(kv: KvStorePort): Promise<void> {
    this.prefs = await loadPrefs(kv);
    this.applyPrefsToBuses();
  }

  /** Persist the current prefs to `kv`. */
  async savePrefs(kv: KvStorePort): Promise<void> {
    await savePrefs(kv, this.prefs);
  }

  /** Master volume 0..1 (clamped). Persists nothing itself — call {@link savePrefs}. */
  setVolume(volume: number): void {
    this.prefs = { ...this.prefs, volume: clampVolume(volume) };
    if (this.masterGain) this.masterGain.gain.value = masterGainFor(this.prefs);
  }

  isMuted(): boolean {
    return this.prefs.muted;
  }

  /** Master mute — a hard gate on the master bus, independent of per-bus enables. */
  setMuted(muted: boolean): void {
    this.prefs = { ...this.prefs, muted };
    if (this.masterGain) this.masterGain.gain.value = masterGainFor(this.prefs);
  }

  /**
   * Enable/disable the MUSIC bus. Enabling guarantees a live source: it restarts
   * the loop if a buffer is loaded (so toggling on after a scene change doesn't
   * leave a silent, source-less bus).
   */
  setMusicEnabled(enabled: boolean): void {
    this.prefs = { ...this.prefs, musicEnabled: enabled };
    this.applyPrefsToBuses();
    if (enabled && this.musicBuffer && !this.musicSource) {
      this.startMusic();
    }
  }

  /** Enable/disable the SFX bus (all cues). */
  setSfxEnabled(enabled: boolean): void {
    this.prefs = { ...this.prefs, sfxEnabled: enabled };
    if (this.sfxGain) this.sfxGain.gain.value = sfxGainFor(this.prefs) * this.sfxLevel;
  }

  /** Independent caller-owned game settings. Master mute remains authoritative;
   * the caller reapplies these normalized channel levels at game boot. */
  setChannelVolumes(sfx: number, music: number): void {
    this.sfxLevel = clampVolume(sfx);
    this.musicLevel = clampVolume(music);
    this.setVolume(1);
    this.setSfxEnabled(this.sfxLevel > 0);
    this.setMusicEnabled(this.musicLevel > 0);
  }

  private musicRestingGain(): number {
    return musicGainFor(this.prefs, this.musicVolume) * this.musicLevel;
  }

  private applyPrefsToBuses(): void {
    if (this.masterGain) this.masterGain.gain.value = masterGainFor(this.prefs);
    if (this.musicGain) {
      // An old duck ramp must not undo a new mute/channel level.
      this.musicGain.gain.cancelScheduledValues(this.ctx?.currentTime ?? 0);
      this.musicGain.gain.value = this.musicRestingGain();
    }
    if (this.sfxGain) this.sfxGain.gain.value = sfxGainFor(this.prefs) * this.sfxLevel;
  }

  // ── Cue registry + playback ────────────────────────────────────

  /**
   * Register (or override) a named PROCEDURAL cue — a native game's own sound.
   * `recipe` MUST be pure, a function only of `(ctx, when, params)`. The
   * platform's vocabulary cues are not registered this way: they are pinned
   * bank samples ({@link registerSampleCue} via `bindSfxBank`).
   */
  registerCue(name: string, recipe: CueRecipe): void {
    this.registry.register(name, recipe);
  }

  /**
   * Register (or override) a SAMPLE-backed cue: the sample ids of its variants,
   * played in rotation by {@link play}. The ids need not be loaded yet — until
   * one is, playing the cue drops (never queues), the same rule as a suspended
   * context.
   */
  registerSampleCue(name: string, ids: readonly string[]): void {
    this.registry.registerSamples(name, ids);
  }

  /** True if a cue is registered under `name` (bank or game-supplied). */
  hasCue(name: string): boolean {
    return this.registry.has(name);
  }

  /** All registered cue names. */
  cueNames(): string[] {
    return this.registry.names();
  }

  /**
   * Play a registered cue now — ONE lookup, whichever kind of source the name
   * holds. No-ops (drops) while the context is suspended, the document is
   * hidden, or the cue name is unregistered — a missing cue is a soft failure,
   * never a throw, so a content typo can't crash gameplay. Returns whether it
   * sounded. A sample cue plays its variants round-robin (`params` do not
   * apply to a pinned sample); a recipe cue receives `params` verbatim.
   */
  play(name: string, params?: CueParams): boolean {
    const source = this.registry.get(name);
    if (!source) {
      this.log?.warn(`audio: unknown cue "${name}"`);
      return false;
    }
    if (source.kind === 'samples') {
      const id = source.ids[source.cursor % source.ids.length]!;
      source.cursor = (source.cursor + 1) % source.ids.length;
      return this.playSample(id);
    }
    if (this.hidden) return false;
    const ctx = this.runningCtx();
    if (!ctx || !this.synth) return false;
    source.recipe(this.synth, ctx.currentTime, params);
    return true;
  }

  // ── Music slot ─────────────────────────────────────────────────

  /** Digest-pinned PCM samples — bank entries and a version's curated
   * selections alike — share this engine's SFX bus and gesture gate. The bytes
   * are hashed HERE and refused unless they hash to `sha256`: missing, invalid
   * or stale samples never fall back to another sound. */
  async loadSample(id: string, bytes: ArrayBuffer, sha256: string): Promise<boolean> {
    if (!SAMPLE_ID.test(id) ||
      (!this.sampleRequests.has(id) && this.sampleRequests.size >= MAX_LOADED_SAMPLES)) return false;
    const request = Symbol(id);
    this.sampleRequests.set(id, request);
    this.samples.delete(id);
    if (bytes.byteLength > MAX_SFX_BYTES || !/^[a-f0-9]{64}$/.test(sha256)) return false;
    const epoch = this.sampleEpoch;
    // Copy before async hashing: callers cannot change bytes between identity
    // verification and decode. Canonical WAV parsing validates bounded memory.
    const owned = bytes.slice(0);
    try {
      const pcm = readPcmSfx(owned);
      const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', owned)), b => b.toString(16).padStart(2, '0')).join('');
      if (hash !== sha256 || epoch !== this.sampleEpoch || this.sampleRequests.get(id) !== request) return false;
      this.init();
      const buffer = this.ctx!.createBuffer(1, pcm.samples.length, pcm.sampleRate);
      buffer.getChannelData(0).set(pcm.samples);
      this.samples.set(id, buffer);
      return true;
    } catch { return false; }
  }

  /**
   * Load an exact, pre-inspected creator upload without relying on browser codec
   * decoding. Invalid replacements deliberately clear their previous sound:
   * content identity is authoritative, never a cue fallback.
   */
  async loadVerifiedAudio(
    id: string,
    bytes: ArrayBuffer,
    sha256: string,
    kind: AudioMediaKind,
    metadata: AudioMediaMetadata,
  ): Promise<boolean> {
    if (!SAMPLE_ID.test(id) || (kind !== 'sfx' && kind !== 'music')) return false;

    if (kind === 'sfx') {
      if (!this.sampleRequests.has(id) && this.sampleRequests.size >= MAX_LOADED_SAMPLES) return false;
      const request = Symbol(id);
      this.sampleRequests.set(id, request);
      this.samples.delete(id);
      if (!/^[a-f0-9]{64}$/.test(sha256) || bytes.byteLength > AUDIO_MEDIA_FORMATS.sfx.maxBytes) return false;
      const epoch = this.sampleEpoch;
      const owned = bytes.slice(0);
      try {
        const inspected = inspectAudioMedia(new Uint8Array(owned), kind, metadata.license, this.loopFromMetadata(metadata));
        const hash = await this.sha256(owned);
        if (hash !== sha256 || !this.sameMetadata(inspected, metadata) || epoch !== this.sampleEpoch || this.sampleRequests.get(id) !== request) return false;
        this.init();
        if (!this.ctx) return false;
        this.samples.set(id, this.pcmBuffer(this.ctx, owned, inspected));
        return true;
      } catch { return false; }
    }

    const epoch = ++this.musicLoadEpoch;
    // A rejected replacement must not leave an earlier track audible.
    this.stopMusic();
    this.musicBuffer = null;
    this.musicLoop = null;
    if (!/^[a-f0-9]{64}$/.test(sha256) || bytes.byteLength !== metadata.bytes || bytes.byteLength > AUDIO_MEDIA_FORMATS.music.maxBytes) return false;
    const owned = bytes.slice(0);
    try {
      const hash = await this.sha256(owned);
      if (hash !== sha256 || epoch !== this.musicLoadEpoch) return false;
      this.init();
      if (!this.ctx || epoch !== this.musicLoadEpoch) return false;
      const buffer = await this.musicBufferFor(this.ctx, owned, metadata);
      if (!buffer || epoch !== this.musicLoadEpoch) return false;
      this.musicBuffer = buffer;
      this.musicLoop = metadata.loopStartMs === undefined ? null : {
        loopStartMs: metadata.loopStartMs,
        loopEndMs: metadata.loopEndMs!,
      };
      return true;
    } catch { return false; }
  }

  /**
   * Decode a verified music artifact, per container. The digest has already been
   * compared to the version-pinned one by the caller, so these bytes ARE the
   * reviewed bytes; what differs is how much the runtime can re-derive from them.
   *
   *  - **`audio/wav`** — unchanged, and deliberately so: the platform's own WAV
   *    parser re-measures the payload and every stored field must match before a
   *    single sample is played. No browser codec is involved.
   *  - **`audio/ogg`** — generated music, decoded by the browser's Opus decoder.
   *    The runtime cannot re-derive duration, peak or loudness here without
   *    shipping a codec into every game bundle, and it does not need to: those
   *    measurements were taken from these exact bytes when the candidate was made
   *    and re-verified against them at publication, and the digest proves the
   *    bytes are the same ones. Byte identity is the authority, as it always was;
   *    what the WAV path adds on top is a bonus a hand-written parser makes free.
   *
   * A browser without Opus support throws in `decodeAudioData`, the caller
   * returns false, and the game plays without music. That is the same
   * fail-closed outcome as any unverifiable audio — never a substituted track.
   */
  private async musicBufferFor(ctx: AudioContext, owned: ArrayBuffer, metadata: AudioMediaMetadata): Promise<AudioBuffer | null> {
    if (metadata.mime === 'audio/ogg') {
      const buffer = await ctx.decodeAudioData(owned.slice(0));
      return buffer.numberOfChannels === metadata.channels ? buffer : null;
    }
    const inspected = inspectAudioMedia(new Uint8Array(owned), 'music', metadata.license, this.loopFromMetadata(metadata));
    return this.sameMetadata(inspected, metadata) ? this.pcmBuffer(ctx, owned, inspected) : null;
  }

  private async sha256(bytes: ArrayBuffer): Promise<string> {
    return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), byte => byte.toString(16).padStart(2, '0')).join('');
  }

  private loopFromMetadata(metadata: AudioMediaMetadata): AudioMediaLoop | undefined {
    return metadata.loopStartMs === undefined && metadata.loopEndMs === undefined
      ? undefined
      : { loopStartMs: metadata.loopStartMs!, loopEndMs: metadata.loopEndMs! };
  }

  private sameMetadata(actual: AudioMediaMetadata, expected: AudioMediaMetadata): boolean {
    return actual.mime === expected.mime && actual.bytes === expected.bytes && actual.durationMs === expected.durationMs &&
      actual.sampleRate === expected.sampleRate && actual.channels === expected.channels && actual.peak === expected.peak &&
      actual.loopStartMs === expected.loopStartMs && actual.loopEndMs === expected.loopEndMs &&
      actual.license.kind === expected.license.kind && actual.license.source === expected.license.source && actual.license.terms === expected.license.terms;
  }

  /** Create channel data directly from the already-validated PCM16 payload. */
  private pcmBuffer(ctx: AudioContext, bytes: ArrayBuffer, metadata: AudioMediaMetadata): AudioBuffer {
    const data = new Uint8Array(bytes);
    const frames = (data.byteLength - 44) / (metadata.channels * 2);
    const buffer = ctx.createBuffer(metadata.channels, frames, metadata.sampleRate);
    for (let channel = 0; channel < metadata.channels; channel += 1) {
      const output = buffer.getChannelData(channel);
      for (let frame = 0; frame < frames; frame += 1) {
        const offset = 44 + (frame * metadata.channels + channel) * 2;
        const raw = data[offset]! | (data[offset + 1]! << 8);
        output[frame] = (raw & 0x8000 ? raw - 0x10000 : raw) / 32_768;
      }
    }
    return buffer;
  }

  /** Play one loaded sample voice now. Drops (never queues) while the shell
   *  has samples suspended, the document is hidden, the voice cap is reached,
   *  the sample is not (yet) loaded, or SFX are muted. */
  playSample(id: string): boolean {
    const buffer = this.samples.get(id);
    if (!buffer || this.samplesSuspended || this.hidden || this.sampleSources.size >= MAX_SAMPLE_VOICES
      || this.prefs.muted || !this.prefs.sfxEnabled) return false;
    const ctx = this.runningCtx();
    if (!ctx || !this.sfxGain) return false;
    const source = ctx.createBufferSource();
    source.buffer = buffer; source.connect(this.sfxGain);
    source.onended = () => { source.disconnect(); this.sampleSources.delete(source); };
    this.sampleSources.set(source, id);
    source.start();
    return true;
  }

  setSamplesSuspended(suspended: boolean): void {
    this.samplesSuspended = suspended;
    if (suspended) this.stopSamples();
  }

  private stopSamples(only?: ReadonlySet<string>): void {
    for (const [source, id] of this.sampleSources) {
      if (only && !only.has(id)) continue;
      source.onended = null;
      try { source.stop(); } catch { /* already ended */ }
      source.disconnect();
      this.sampleSources.delete(source);
    }
  }

  /**
   * Release SPECIFIC samples — a departed version's curated selections — while
   * everything else stays loaded (the bank binding lives as long as the
   * engine). Their voices stop now, and an in-flight load of a released id is
   * refused when it completes, because its request token is gone.
   */
  unloadSamples(ids: Iterable<string>): void {
    const gone = new Set(ids);
    if (gone.size === 0) return;
    this.stopSamples(gone);
    for (const id of gone) {
      this.samples.delete(id);
      this.sampleRequests.delete(id);
    }
  }

  /** Release EVERY sample and invalidate every in-flight load (disposal, and a
   *  preview surface that rebinds from scratch). */
  clearSamples(): void {
    this.sampleEpoch++;
    this.stopSamples();
    this.samples.clear();
    this.sampleRequests.clear();
  }

  /**
   * Provide the looping-music buffer. Either a decoded {@link AudioBuffer} or an
   * encoded byte source the engine decodes through the context. Replaces any
   * previously loaded track (does not auto-start — call {@link startMusic}).
   */
  async loadMusic(source: AudioBuffer | ArrayBuffer): Promise<void> {
    this.musicLoop = null;
    await this.decodeMusic(source, ++this.musicLoadEpoch);
  }

  private async decodeMusic(source: AudioBuffer | ArrayBuffer, epoch: number): Promise<void> {
    // A slow fetch must not recreate a disposed context or supersede a newer
    // track choice. Decode completion is fenced separately below.
    if (epoch !== this.musicLoadEpoch) return;
    this.init();
    const ctx = this.ctx;
    if (!ctx) return;
    const buffer = source instanceof ArrayBuffer ? await ctx.decodeAudioData(source.slice(0)) : source;
    if (epoch !== this.musicLoadEpoch || ctx !== this.ctx) return;
    this.stopMusic();
    this.musicBuffer = buffer;
  }

  /**
   * Fetch + decode a music track from `url` through the provided `fetchFn`
   * (injected so this stays DOM-free and testable; defaults to global `fetch`).
   */
  async loadMusicUrl(
    url: string,
    fetchFn: (u: string) => Promise<{ arrayBuffer(): Promise<ArrayBuffer> }> = fetch,
  ): Promise<void> {
    const epoch = ++this.musicLoadEpoch;
    this.musicLoop = null;
    try {
      const res = await fetchFn(url);
      const bytes = await res.arrayBuffer();
      await this.decodeMusic(bytes, epoch);
    } catch (e) {
      // Keep the previous valid track on failure. A stale request cannot
      // change playback state or report failure for the new selection.
      if (epoch === this.musicLoadEpoch) this.log?.warn(`audio: music load failed for ${url}`, e);
    }
  }

  /**
   * Start (or restart) the looping music through the music bus. Idempotent while
   * already playing. If no buffer is loaded yet this is a no-op; load first via
   * {@link loadMusic}/{@link loadMusicUrl}. The source starts even while the
   * context is suspended — it begins on the first gesture-driven resume.
   */
  startMusic(): void {
    if (this.playbackGated()) return;
    this.init();
    if (!this.ctx || !this.musicGain || !this.musicBuffer) return;
    if (this.musicPlaying && this.musicSource) return;
    this.playMusicSource();
  }

  private playMusicSource(): void {
    if (!this.ctx || !this.musicGain || !this.musicBuffer) return;
    try {
      this.musicSource?.stop();
    } catch {
      /* not started */
    }
    const src = this.ctx.createBufferSource();
    src.buffer = this.musicBuffer;
    src.loop = true;
    if (this.musicLoop) {
      src.loopStart = this.musicLoop.loopStartMs / 1_000;
      src.loopEnd = this.musicLoop.loopEndMs / 1_000;
    }
    src.connect(this.musicGain);
    src.start();
    this.musicSource = src;
    this.musicPlaying = true;
  }

  /** Stop the looping music. */
  stopMusic(): void {
    this.resumeMusic = false;
    this.musicPlaying = false;
    if (this.duckTimer) {
      clearTimeout(this.duckTimer);
      this.duckTimer = null;
    }
    if (this.musicGain) {
      this.musicGain.gain.cancelScheduledValues(this.ctx?.currentTime ?? 0);
      this.musicGain.gain.value = this.musicRestingGain();
    }
    try {
      this.musicSource?.stop();
    } catch {
      /* not started */
    }
    this.musicSource = null;
  }

  /** The shell's lifecycle gate (pause, recovery, demo, departed scene),
   * independent of the player's music preference. Only a loop that was playing
   * before a gate went up may resume, and only once EVERY gate is down;
   * requests while gated are dropped. Replacing/unloading the track cancels
   * that resume intent. */
  setMusicSuspended(suspended: boolean): void {
    if (suspended === this.musicSuspended) return;
    this.musicSuspended = suspended;
    this.applyPlaybackGate();
  }

  /** The document-visibility gate: a hidden tab is silent. Music stops (and
   * remembers to resume), live sample voices stop, and cues drop until the
   * tab is visible again. Composed with {@link setMusicSuspended} — showing
   * the tab does not resume music the shell still has paused. */
  setDocumentHidden(hidden: boolean): void {
    if (hidden === this.hidden) return;
    this.hidden = hidden;
    if (hidden) this.stopSamples();
    this.applyPlaybackGate();
  }

  private playbackGated(): boolean {
    return this.musicSuspended || this.hidden;
  }

  /** One rule for both gates: going up stops an audible loop and records the
   *  intent to resume; the intent survives other gates going up and down, and
   *  is spent only when none is left. */
  private applyPlaybackGate(): void {
    if (this.playbackGated()) {
      if (this.musicPlaying) {
        this.stopMusic();
        this.resumeMusic = true;
      }
      return;
    }
    const shouldResume = this.resumeMusic;
    this.resumeMusic = false;
    if (shouldResume && this.prefs.musicEnabled && !this.prefs.muted) this.startMusic();
  }

  /** Release a version-owned track without disposing the app-level engine.
   * Pending fetch/decode completions cannot restore a departed version. */
  unloadMusic(): void {
    this.musicLoadEpoch++;
    this.stopMusic();
    this.musicBuffer = null;
    this.musicLoop = null;
  }

  /** Is the music loop currently playing? */
  isMusicPlaying(): boolean {
    return this.musicPlaying;
  }

  // ── Ducking ────────────────────────────────────────────────────

  /**
   * Duck the music bus down to `factor` (of its enabled resting gain) for
   * `durationSec`, then ramp back — used to make a transient cue (a stinger, a
   * rank-up) cut through the loop. No-op while suspended or music-disabled.
   * Overlapping ducks reset the restore timer (the deepest active duck wins by
   * simple last-write; the shared restore ramps back to the enabled resting gain).
   */
  duckMusic(durationSec = 0.4, factor = 0.3): void {
    const ctx = this.runningCtx();
    const bus = this.musicGain;
    if (!ctx || !bus || !this.prefs.musicEnabled) return;

    const resting = this.musicRestingGain();
    const ducked = clampVolume(resting * clampVolume(factor));
    const now = ctx.currentTime;
    bus.gain.cancelScheduledValues(now);
    bus.gain.setValueAtTime(Math.max(ducked, 0.0001), now);
    // Ramp back up over the tail of the window.
    bus.gain.exponentialRampToValueAtTime(
      Math.max(resting, 0.0001),
      now + Math.max(durationSec, 0.01),
    );

    if (this.duckTimer) clearTimeout(this.duckTimer);
    this.duckTimer = setTimeout(() => {
      this.duckTimer = null;
      // Snap to the exact resting gain in case prefs changed mid-duck.
      if (this.musicGain) {
        this.musicGain.gain.value = this.musicRestingGain();
      }
    }, Math.max(durationSec, 0.01) * 1000);
  }
}
